import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../client/supabaseClient";
import AuthRedirect from "../client/AuthRedirect";
import { useNavigate } from "react-router-dom";

import {
    Box,
    Button,
    Container,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    Paper,
} from "@mui/material";
import { Visibility, VisibilityOff, Home, Login } from "@mui/icons-material";

const SignIn = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [formValues, setFormValues] = useState({
        email: "",
        password: "",
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues({ ...formValues, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setStatus("Logging in...");
        setErrorMessage("");
        setLoading(true);

        const { error } = await supabase.auth.signInWithPassword({
            email: formValues.email,
            password: formValues.password,
        });

        if (error) {
            setErrorMessage(error.message);
        } else {
            setStatus("Login successful! Redirecting...");
            navigate("/");
        }

        setLoading(false);
    };

    return (
        <AuthRedirect>
            <Container maxWidth="sm">
                <Paper elevation={4} sx={{ p: 4, mt: 6, borderRadius: 3 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Home sx={{ mr: 1, color: "primary.main" }} />
                        <Link to="/" style={{ textDecoration: "none", color: "#1976d2" }}>
                            Home
                        </Link>
                    </Box>

                    <Typography variant="h4" align="center" gutterBottom>
                        Sign In
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Email"
                            name="email"
                            type="email"
                            value={formValues.email}
                            onChange={handleInputChange}
                            required
                        />

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formValues.password}
                            onChange={handleInputChange}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {errorMessage && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {errorMessage}
                            </Alert>
                        )}

                        {status && !errorMessage && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                {status}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3 }}
                            startIcon={loading ? <CircularProgress size={20} /> : <Login />}
                            disabled={loading}
                        >
                            {loading ? "Logging in..." : "Login"}
                        </Button>
                    </form>

                    <Typography align="center" sx={{ mt: 2 }}>
                        Don’t have an account?{" "}
                        <Link to="/sign-up" style={{ color: "#1976d2" }}>
                            Sign Up
                        </Link>
                    </Typography>
                </Paper>
            </Container>
        </AuthRedirect>
    );
};

export default SignIn;
